import React from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { CheckCircle, TrendingUp, Users, FileText, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

const LandingPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const features = [
    { icon: TrendingUp, title: 'Sales Pipeline', description: 'Track projects from inquiry to completion' },
    { icon: Users, title: 'HR Management', description: 'Attendance, overtime, and residence tracking' },
    { icon: FileText, title: 'Document Management', description: 'Store and manage all project files securely' },
  ];

  return (
    <>
      <Helmet>
        <title>Stone Beam Projects Portal - Project Management Reimagined</title>
        <meta name="description" content="Complete project management and HR portal for Stone Beam Demolition" />
      </Helmet>

      <div className="min-h-screen bg-slate-50">
        {/* Navigation */}
        <nav className="bg-white shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-600 rounded-lg" />
                <span className="text-xl font-bold text-slate-900">Stone Beam</span>
              </div>
              <div className="flex items-center gap-4">
                {user ? (
                  <Button onClick={() => navigate('/dashboard')} className="bg-orange-600 hover:bg-orange-700">
                    Go to Dashboard
                  </Button>
                ) : (
                  <Button onClick={() => navigate('/login')} variant="outline" className="border-orange-600 text-orange-600 hover:bg-orange-50">
                    Login
                  </Button>
                )}
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-orange-50 to-slate-100" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-center"
            >
              <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
                Project Management
                <span className="block text-orange-600">Reimagined</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Complete solution for managing projects, teams, and operations in one powerful platform
              </p>
              <div className="flex justify-center gap-4">
                {user ? (
                  <Button 
                    onClick={() => navigate('/dashboard')} 
                    size="lg"
                    className="bg-orange-600 hover:bg-orange-700"
                  >
                    Go to Dashboard
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                ) : (
                  <>
                    <Button 
                      onClick={() => navigate('/login')} 
                      size="lg"
                      className="bg-orange-600 hover:bg-orange-700"
                    >
                      Get Started
                      <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    <Button 
                      onClick={() => navigate('/login')} 
                      size="lg"
                      variant="outline"
                      className="border-orange-600 text-orange-600 hover:bg-orange-50"
                    >
                      Login
                    </Button>
                  </>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Everything You Need</h2>
              <p className="text-lg text-slate-600">Powerful features to streamline your operations</p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  className="bg-slate-50 rounded-2xl p-8 hover:shadow-lg transition-shadow"
                >
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                    <feature.icon className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-orange-600 to-orange-700">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">Ready to Get Started?</h2>
            <p className="text-xl text-orange-100 mb-8">
              Join Stone Beam and transform how you manage projects
            </p>
            {!user && (
              <Button 
                onClick={() => navigate('/login')} 
                size="lg"
                className="bg-white text-orange-600 hover:bg-slate-50"
              >
                Start Now
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            )}
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-slate-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-slate-400">© 2026 Stone Beam Demolition. All rights reserved.</p>
          </div>
        </footer>
      </div>
    </>
  );
};

export default LandingPage;